import { createReducer, on } from '@ngrx/store';
import { Account } from 'src/app/models/account.model';
import { Deposit } from 'src/app/models/deposit.model';
import { EBillsServiceModel } from 'src/app/models/e-bills-service.model';
import { eBillsModel } from 'src/app/models/e-bills.model';
import { FilterEBillsServices } from 'src/app/models/filter-e-bills-services.model';
import { Loan } from 'src/app/models/loan.model';
import { Saving } from 'src/app/models/saving.model';
import { SDDLimitDetails } from 'src/app/models/sdd-limit-details.model';
import { SDDTransactionDetails } from 'src/app/models/sdd-transaction-details.model';
import { AccountDetails } from '../../../models/account-details.model';
import { Statement } from '../../../models/statement.model';
import * as fromAccountsActions from '../../actions/accounts/accounts.actions';
import { AccountTransactionFilter } from './../../../models/account-transaction-filter.model';
import { SavingsDetails } from './../../../models/saving-details.model';
import { Transaction } from './../../../models/transaction.model';
import {
  createServerDate,
  generateFutureDate,
  generatePastDate
} from './../../../shared/utils/date.utils';
import { FilterEBillsSubscribed } from 'src/app/models/filter-e-bills-subscribed.model';
import { EBillsRelationshipModel } from 'src/app/models/e-bills-relationship.model';

export const accountFeatureKey = 'accounts';

export interface State {
  selectedAccount: Account | null;
  selectedLoan: Loan | null;
  selectedSaving: Saving | null;
  selectedPrepaid: Account | null;
  selectedDeposit: Deposit | null;
  accountDetails: AccountDetails | null;
  savingDetails: SavingsDetails | null;
  depositDetails: SavingsDetails | null;
  accountTransactions: Transaction[] | null;
  savingTransactions: Transaction[] | null;
  accountAnnouncements: Transaction[] | null;
  eBillsList: eBillsModel[] | null;
  eBillsDetails: eBillsModel | null;
  eBillsFileDetails: any | null;
  sddList: SDDTransactionDetails[] | null;
  sddDetails: SDDTransactionDetails | null;
  sddLimits: SDDLimitDetails[] | null;
  sddLimitsDetails: SDDLimitDetails | null;
  eBillsServicesList: EBillsServiceModel[] | null;
  eBillsSubscribedList: EBillsRelationshipModel[] | null;
  eBillsServiceDetails: EBillsServiceModel | null;
  accountFilterEBills: string | null;
  receivedSentEnum: string | null;
  sddTransactionType: string | null;
  unsignedOrders: Transaction[] | null;
  accountTransactionDetails: Transaction | null;
  savingsTransactionDetails: Transaction | null;
  eBillsServicesFilters: FilterEBillsServices | null;
  eBillsSubscribedFilters: FilterEBillsSubscribed | null;
  accountTransactionsFilters: AccountTransactionFilter;
  accountRetailTransactionsFilters: AccountTransactionFilter;
  accountAnnouncementsFilters: AccountTransactionFilter;
  accountStatementsFilters: AccountTransactionFilter;
  savingsTransactionsFilters: AccountTransactionFilter;
  statements: Statement[] | null;
  filtersApplied: number;
  selectedOrder: Transaction[];
  accountUnsignedOrdersFilters: AccountTransactionFilter | null;
  error: any;
  setPrepaidIban: any;
}

export const initialState: State = {
  selectedAccount: null,
  setPrepaidIban: null,
  selectedLoan: null,
  selectedSaving: null,
  selectedPrepaid: null,
  selectedDeposit: null,
  accountDetails: null,
  savingDetails: null,
  depositDetails: null,
  accountTransactions: null,
  savingTransactions: null,
  accountAnnouncements: null,
  eBillsList: null,
  sddList: null,
  sddDetails: null,
  sddLimits: null,
  sddLimitsDetails: null,
  eBillsServicesList: null,
  eBillsSubscribedList: null,
  eBillsSubscribedFilters: null,
  accountFilterEBills: null,
  receivedSentEnum: null,
  sddTransactionType: null,
  eBillsServicesFilters: null,
  eBillsDetails: null,
  eBillsFileDetails: null,
  eBillsServiceDetails: null,
  unsignedOrders: null,
  statements: null,
  selectedOrder: [],
  accountUnsignedOrdersFilters: {
    paymentDateTo: createServerDate(generateFutureDate(0, 0, 0).toString()),
    paymentDateFrom: createServerDate(generatePastDate(1, 1, 0).toString()),
    paymentAmountFrom: '',
    paymentAmountTo: '',
    iban: '',
    payerPayee: '',
    description: ''
  },
  accountTransactionDetails: null,
  savingsTransactionDetails: null,
  accountTransactionsFilters: {
    accountId: '',
    paymentDateTo: createServerDate(generateFutureDate(0, 1, 0).toString()),
    paymentDateFrom: createServerDate(generatePastDate(-1, 2, 0).toString())
  },
  accountRetailTransactionsFilters: {
    accountId: '',
    paymentDateTo: createServerDate(generateFutureDate(0, 0, 0).toString()),
    paymentDateFrom: createServerDate(generatePastDate(-1, 3, 0).toString())
  },

  accountAnnouncementsFilters: {
    paymentDateTo: createServerDate(generateFutureDate(-1, 3, 0).toString()),
    paymentDateFrom: createServerDate(generatePastDate(-1, 0, 0).toString())
  },
  accountStatementsFilters: {
    paymentDateTo: createServerDate(generateFutureDate(0, 0, 0).toString()),
    paymentDateFrom: createServerDate(generatePastDate(1, 1, 0).toString())
  },
  savingsTransactionsFilters: {
    accountId: '',
    paymentDateTo: createServerDate(generateFutureDate(0, 1, 0).toString()),
    paymentDateFrom: createServerDate(generatePastDate(-1, 2, 0).toString())
  },
  filtersApplied: 0,
  error: null
};

export const reducer = createReducer(
  initialState,
  on(
    fromAccountsActions.layoutSelectActiveAccount,
    fromAccountsActions.accountsComponentSelectActiveAccount,
    fromAccountsActions.cardDropdownComponentSelectActiveAccount,
    fromAccountsActions.paymentsComponentSelectActiveAccount,
    fromAccountsActions.standardAccountSelectActiveAccount,
    (state, action) => {
      return {
        ...state,
        selectedAccount: action.selectedAccount
      };
    }
  ),
  on(
    fromAccountsActions.layoutSelectActiveSaving,
    fromAccountsActions.standardAccountSelectActiveSaving,
    fromAccountsActions.savingsComponentSelectActiveAccount,
    (state, action) => {
      return {
        ...state,
        selectedSaving: action.selectedSaving
      };
    }
  ),

  on(
    fromAccountsActions.layoutSelectActivePrepaid,
    fromAccountsActions.standardAccountSelectActivePrepaid,
    fromAccountsActions.prepaidComponentSelectActiveAccount,
    (state, action) => {
      return {
        ...state,
        selectedPrepaid: action.selectedPrepaid
      };
    }
  ),
  on(
    fromAccountsActions.layoutSelectActiveDeposit,
    fromAccountsActions.standardAccountSelectActiveDeposit,
    fromAccountsActions.depositsComponentSelectActiveAccount,
    (state, action) => {
      return {
        ...state,
        selectedDeposit: action.selectedDeposit
      };
    }
  ),
  on(
    fromAccountsActions.layoutSelectActiveLoan,
    fromAccountsActions.loansComponentSelectActiveAccount,
    fromAccountsActions.standardAccountSelectActiveLoan,
    (state, action) => {
      return {
        ...state,
        selectedLoan: action.selectedLoan
      };
    }
  ),
  on(fromAccountsActions.loadAccountDetailsSuccess, (state, action) => {
    return {
      ...state,
      accountDetails: action.accountDetails
    };
  }),
  on(fromAccountsActions.loadSavingDetailsSuccess, (state, action) => {
    return {
      ...state,
      savingDetails: action.savingDetails
    };
  }),
  on(fromAccountsActions.loadDepositDetailsSuccess, (state, action) => {
    return {
      ...state,
      depositDetails: action.depositDetails
    };
  }),
  on(fromAccountsActions.loadTransactionHistorySuccess, (state, action) => {
    return {
      ...state,
      accountTransactions: action.accountTransactions
    };
  }),
  on(
    fromAccountsActions.loadSavingsTransactionHistorySuccess,
    (state, action) => {
      return {
        ...state,
        savingTransactions: action.savingsTransactions
      };
    }
  ),
  on(fromAccountsActions.loadAnnouncementHistorySuccess, (state, action) => {
    return {
      ...state,
      accountAnnouncements: action.accountAnnouncements
    };
  }),
  on(fromAccountsActions.resetTransactionHistory, state => {
    return {
      ...state,
      accountTransactions: null
    };
  }),
  on(fromAccountsActions.loadUnsignedOrdersSuccess, (state, action) => {
    return {
      ...state,
      unsignedOrders: action.unsignedOrders
    };
  }),
  on(fromAccountsActions.resetUnsignedOrders, (state, action) => {
    return {
      ...state,
      unsignedOrders: null
    };
  }),
  on(fromAccountsActions.setAccountTransactionDetails, (state, action) => {
    return {
      ...state,
      accountTransactionDetails: action.accountTransactionDetails
    };
  }),
  on(fromAccountsActions.setSavingsTransactionDetails, (state, action) => {
    return {
      ...state,
      savingsTransactionDetails: action.savingsTransactionDetails
    };
  }),
  on(fromAccountsActions.setPrepaidIban, (state, action) => {
    return {
      ...state,
      setPrepaidIban: action.prepaidIban
    };
  }),
  on(fromAccountsActions.loadEBillsListSuccess, (state, action) => {
    return {
      ...state,
      eBillsList: action.eBillTransactions
    };
  }),
  on(fromAccountsActions.loadSDDListSuccess, (state, action) => {
    return {
      ...state,
      sddList: action.sddTransactions
    };
  }),
  on(fromAccountsActions.loadSDDListFailure, (state, action) => {
    return {
      ...state,
      sddList: []
    };
  }),
  on(fromAccountsActions.loadSDDLimitsSuccess, (state, action) => {
    return {
      ...state,
      sddLimits: action.sddLimits
    };
  }),
  on(fromAccountsActions.loadSDDLimitsFailure, (state, action) => {
    return {
      ...state,
      sddLimits: []
    };
  }),
  on(fromAccountsActions.loadEBillsServicesListSuccess, (state, action) => {
    return {
      ...state,
      eBillsServicesList: action.eBillsServicesList
    };
  }),
  on(fromAccountsActions.loadEBillsSubscribedListSuccess, (state, action) => {
    return {
      ...state,
      eBillsSubscribedList: action.eBillsSubscribedList
    };
  }),
  on(fromAccountsActions.accountFilterEBills, (state, action) => {
    return {
      ...state,
      accountFilterEBills: action.paymentStatus,
      receivedSentEnum: action.receivedSentEnum
    };
  }),
  on(fromAccountsActions.sddTransactionTypeFilters, (state, action) => {
    return {
      ...state,
      sddTransactionType: action.sddTransactionType
    };
  }),
  on(fromAccountsActions.setSDDDetails, (state, action) => {
    return {
      ...state,
      sddDetails: action.sddDetails
    };
  }),
  on(fromAccountsActions.setSDDLimitsDetails, (state, action) => {
    return {
      ...state,
      sddLimitsDetails: action.sddLimitsDetails
    };
  }),
  on(fromAccountsActions.setEBillsDetails, (state, action) => {
    return {
      ...state,
      eBillsDetails: action.eBillsDetails
    };
  }),
  on(fromAccountsActions.setEBillsFileDetails, (state, action) => {
    return {
      ...state,
      eBillsFileDetails: action.eBillsFileDetails
    };
  }),
  on(fromAccountsActions.setEBillsServiceDetails, (state, action) => {
    return {
      ...state,
      eBillsServiceDetails: action.eBillsServiceDetails
    };
  }),
  on(fromAccountsActions.resetAccountTransactionDetails, state => {
    return {
      ...state,
      accountTransactionDetails: null
    };
  }),
  on(fromAccountsActions.resetSavingsTransactionDetails, state => {
    return {
      ...state,
      savingsTransactionDetails: null
    };
  }),
  on(
    fromAccountsActions.accountFilterTransaction,
    fromAccountsActions.accountFilterTransactionFromDetails,
    fromAccountsActions.accountFilterTransactionFromAccounts,
    (state, action) => {
      let filtersApplied = 0;
      const formVal = action.accountTransactionsFilters;
      Object.keys(formVal).map((key: string) => {
        switch (key) {
          case 'paymentDateFrom':
            formVal[key] !==
            createServerDate(generatePastDate(-1, 2, 0).toString())
              ? filtersApplied++
              : '';
            break;
          case 'paymentDateTo':
            formVal[key] !==
            createServerDate(generateFutureDate(0, 1, 0).toString())
              ? filtersApplied++
              : '';
            break;
          case 'modelDebtor':
            formVal[key] && formVal.callDebtor ? filtersApplied++ : '';
            break;
          case 'modelCreditor':
            formVal[key] && formVal.callCreditor ? filtersApplied++ : '';
            break;
          case 'callCreditor':
          case 'callDebtor':
            break;
          default:
            (formVal as any)[key] ? filtersApplied++ : '';
        }
      });

      return {
        ...state,
        accountTransactionsFilters: action.accountTransactionsFilters,
        filtersApplied
      };
    }
  ),
  on(
    fromAccountsActions.accountFilterTransactionFromDetails,
    fromAccountsActions.accountFilterTransactionFromAccounts,
    (state, action) => {
      let filtersApplied = 0;
      const formVal = action.accountTransactionsFilters;
      Object.keys(formVal).map((key: string) => {
        switch (key) {
          case 'paymentDateFrom':
            formVal[key] !==
            createServerDate(generatePastDate(-1, 3, 0).toString())
              ? filtersApplied++
              : '';
            break;
          case 'paymentDateTo':
            formVal[key] !==
            createServerDate(generateFutureDate(0, 0, 0).toString())
              ? filtersApplied++
              : '';
            break;
          case 'modelDebtor':
            formVal[key] && formVal.callDebtor ? filtersApplied++ : '';
            break;
          case 'modelCreditor':
            formVal[key] && formVal.callCreditor ? filtersApplied++ : '';
            break;
          case 'callCreditor':
          case 'callDebtor':
            break;
          default:
            (formVal as any)[key] ? filtersApplied++ : '';
        }
      });

      return {
        ...state,
        accountTransactionsFilters: action.accountTransactionsFilters,
        filtersApplied
      };
    }
  ),
  on(fromAccountsActions.filterEBillsServices, (state, action) => {
    let filtersApplied = 0;
    return {
      ...state,
      eBillsServicesFilters: action.eBillsServicesFilters,
      filtersApplied
    };
  }),
  on(fromAccountsActions.filterEBillsSubscribed, (state, action) => {
    let filtersApplied = 0;
    return {
      ...state,
      eBillsSubscribedFilters: action.eBillsSubscribedFilters,
      filtersApplied
    };
  }),
  on(fromAccountsActions.accountFilterAnnouncements, (state, action) => {
    let filtersApplied = 0;
    const formVal = action.accountAnnouncementsFilters;
    Object.keys(formVal).map((key: string) => {
      switch (key) {
        case 'paymentDateFrom':
          formVal[key] !==
          createServerDate(generatePastDate(-1, 0, 0).toString())
            ? filtersApplied++
            : '';
          break;
        case 'paymentDateTo':
          formVal[key] !==
          createServerDate(generateFutureDate(-1, 3, 0).toString())
            ? filtersApplied++
            : '';
          break;
        case 'modelDebtor':
          formVal[key] && formVal.callDebtor ? filtersApplied++ : '';
          break;
        case 'modelCreditor':
          formVal[key] && formVal.callCreditor ? filtersApplied++ : '';
          break;
        case 'callCreditor':
        case 'callDebtor':
          break;
        default:
          (formVal as any)[key] ? filtersApplied++ : '';
      }
    });
    return {
      ...state,
      accountAnnouncementsFilters: action.accountAnnouncementsFilters,
      filtersApplied
    };
  }),
  on(fromAccountsActions.savingsFilterTransaction, (state, action) => {
    let filtersApplied = 0;
    const formVal = action.savingsTransactionsFilters;
    Object.keys(formVal).map((key: string) => {
      switch (key) {
        case 'paymentDateFrom':
          formVal[key] !==
          createServerDate(generatePastDate(-1, 0, 0).toString())
            ? filtersApplied++
            : '';
          break;
        case 'paymentDateTo':
          formVal[key] !==
          createServerDate(generateFutureDate(-1, 3, 0).toString())
            ? filtersApplied++
            : '';
          break;
        default:
          (formVal as any)[key] ? filtersApplied++ : '';
      }
    });
    return {
      ...state,
      savingsTransactionsFilters: action.savingsTransactionsFilters,
      filtersApplied
    };
  }),
  on(fromAccountsActions.resetFilterTransaction, state => {
    return {
      ...state,
      accountTransactionsFilters: {
        paymentDateTo: createServerDate(generateFutureDate(0, 1, 0).toString()),
        paymentDateFrom: createServerDate(generatePastDate(-1, 2, 0).toString())
      },
      filtersApplied: 0
    };
  }),

  on(fromAccountsActions.resetAnnouncementFilterTransaction, state => {
    return {
      ...state,
      accountAnnouncementsFilters: {
        paymentDateTo: createServerDate(
          generateFutureDate(-1, 3, 0).toString()
        ),
        paymentDateFrom: createServerDate(generatePastDate(-1, 0, 0).toString())
      },
      filtersApplied: 0
    };
  }),
  on(fromAccountsActions.resetFilterSavingsTransaction, state => {
    return {
      ...state,
      savingsTransactionsFilters: {
        paymentDateTo: createServerDate(generateFutureDate(0, 1, 0).toString()),
        paymentDateFrom: createServerDate(generatePastDate(-1, 2, 0).toString())
      },
      filtersApplied: 0
    };
  }),
  on(fromAccountsActions.accountFilterStatements, (state, action) => {
    let filtersApplied = 0;
    const formVal = action.accountStatementsFilters;
    Object.keys(formVal).map((key: string) => {
      switch (key) {
        case 'paymentDateFrom':
          formVal[key] !==
          createServerDate(generatePastDate(1, 1, 0).toString())
            ? filtersApplied++
            : '';
          break;
        case 'paymentDateTo':
          formVal[key] !==
          createServerDate(generateFutureDate(0, 0, 0).toString())
            ? filtersApplied++
            : '';
          break;
        default:
          (formVal as any)[key] ? filtersApplied++ : '';
      }
    });
    return {
      ...state,
      accountStatementsFilters: action.accountStatementsFilters,
      filtersApplied
    };
  }),
  on(fromAccountsActions.resetFilterStatements, state => {
    return {
      ...state,
      accountStatementsFilters: {
        paymentDateTo: createServerDate(generateFutureDate(0, 0, 0).toString()),
        paymentDateFrom: createServerDate(generatePastDate(1, 1, 0).toString())
      },
      filtersApplied: 0
    };
  }),
  on(fromAccountsActions.loadAccountStatementsSuccess, (state, action) => {
    return {
      ...state,
      statements: action.accountStatements,
      error: null
    };
  }),
  on(fromAccountsActions.resetAccountStatements, (state, action) => {
    return {
      ...state,
      statements: null,
      error: null
    };
  }),
  on(fromAccountsActions.loadAccountStatementsFailure, (state, action) => {
    return {
      ...state,
      statements: null,
      error: action.error
    };
  }),
  on(fromAccountsActions.changeAccountActiveCurrency, (state, action) => {
    return {
      ...state,
      selectedAccount: state.selectedAccount
        ? {
            ...state.selectedAccount,
            activeBalance: action.activeBalance
          }
        : null
    };
  }),
  on(fromAccountsActions.changeSavingActiveCurrency, (state, action) => {
    return {
      ...state,
      selectedSaving: state.selectedSaving
        ? {
            ...state.selectedSaving,
            activeBalance: action.activeBalance
          }
        : null
    };
  }),
  on(fromAccountsActions.changeLoanActiveCurrency, (state, action) => {
    return {
      ...state,
      selectedLoan: state.selectedLoan
        ? {
            ...state.selectedLoan,
            activeBalance: action.activeBalance
          }
        : null
    };
  }),
  on(fromAccountsActions.changeDepositActiveCurrency, (state, action) => {
    return {
      ...state,
      selectedDeposit: state.selectedDeposit
        ? {
            ...state.selectedDeposit,
            activeBalance: action.activeBalance
          }
        : null
    };
  }),
  on(fromAccountsActions.selectedAccountUnsignedOrders, (state, action) => {
    const getStateSelectOrder = [...state.selectedOrder];
    if (action.data) {
      const selectedOrderIndex = getStateSelectOrder.indexOf(action.data);
      action.data.isChecked
        ? getStateSelectOrder.push(action.data)
        : getStateSelectOrder.splice(selectedOrderIndex, 1);
    }
    return {
      ...state,
      selectedOrder: getStateSelectOrder
    };
  }),
  on(
    fromAccountsActions.resetSelectedAccountUnsignedOrders,
    (state, action) => {
      return {
        ...state,
        selectedOrder: []
      };
    }
  ),
  on(fromAccountsActions.accountFilterUnsignedOrders, (state, action) => {
    let filtersApplied = 0;
    const formVal = action.accountUnsignedOrdersFilters;
    Object.keys(formVal).map((key: string) => {
      switch (key) {
        case 'paymentDateFrom':
          formVal[key] !==
          createServerDate(generatePastDate(1, 1, 0).toString())
            ? filtersApplied++
            : '';
          break;
        case 'paymentDateTo':
          formVal[key] !==
          createServerDate(generateFutureDate(0, 0, 0).toString())
            ? filtersApplied++
            : '';
          break;
        default:
          (formVal as any)[key] ? filtersApplied++ : '';
      }
    });
    return {
      ...state,
      accountUnsignedOrdersFilters: action.accountUnsignedOrdersFilters,
      filtersApplied
    };
  }),
  on(fromAccountsActions.resetFilterUnsignedOrders, state => {
    return {
      ...state,
      accountUnsignedOrdersFilters: {
        paymentDateTo: createServerDate(generateFutureDate(0, 0, 0).toString()),
        paymentDateFrom: createServerDate(generatePastDate(1, 1, 0).toString()),
        paymentAmountFrom: '',
        paymentAmountTo: '',
        iban: '',
        payerPayee: '',
        description: ''
      }
    };
  })
);
